/**
 * PreventiApp Observation Engine v1.0 — datos oficiales para la app móvil.
 *
 * ⚠️ NO MODIFICAR sin actualizar el documento maestro
 *    "PreventiApp_Observation_Engine_v1_0.md".
 *
 * Esta es la única fuente de verdad: dominios, conductas observables,
 * variables (frecuencia/intensidad/duración/contexto/participantes/fuente),
 * eventos centinela y matriz de priorización.
 */

// ============ 6 DOMINIOS OFICIALES (§4) ============
export const DOMAINS = [
  {
    id: 'D1',
    name: 'Regulación emocional',
    full: 'Regulación emocional y bienestar psicológico',
    microcopy: 'Llanto, aislamiento, crisis visible.',
    color: '#C98718',
    bg: '#FFF1D6',
    soft: '#FFF9EC',
    border: '#F6B73C',
  },
  {
    id: 'D2',
    name: 'Violencia entre pares',
    full: 'Violencia entre pares y convivencia',
    microcopy: 'Acoso, exclusión, ciberacoso.',
    color: '#D86B26',
    bg: '#FFE7D6',
    soft: '#FFF1E6',
    border: '#FF8A47',
  },
  {
    id: 'D3',
    name: 'Seguridad escolar',
    full: 'Riesgo para la seguridad escolar',
    microcopy: 'Amenazas, agresión, objetos peligrosos.',
    color: '#C73838',
    bg: '#FCDCDC',
    soft: '#FFECEC',
    border: '#E64545',
  },
  {
    id: 'D4',
    name: 'Riesgo individual',
    full: 'Riesgo individual significativo',
    microcopy: 'Autolesión, mensajes de despedida.',
    color: '#5C3FB8',
    bg: '#ECE3FF',
    soft: '#F4EEFF',
    border: '#7B5CD6',
  },
  {
    id: 'D5',
    name: 'Vulneración de derechos',
    full: 'Vulneración de derechos',
    microcopy: 'Negligencia, maltrato, desprotección.',
    color: '#0B1B5E',
    bg: '#DCE2F5',
    soft: '#EAEEF9',
    border: '#2E3F8B',
  },
  {
    id: 'D6',
    name: 'Clima relacional',
    full: 'Clima relacional y factores contextuales',
    microcopy: 'Conflictos grupales, rechazo, polarización.',
    color: '#008888',
    bg: '#D6F3F3',
    soft: '#EAFAFA',
    border: '#00A6A6',
  },
];

// ============ CONDUCTAS OBSERVABLES POR DOMINIO (§5–§10) ============
export const CONDUCTS = {
  D1: [
    { code: 'D1-C01', label: 'Llanto persistente',                 base: 'Bajo/Moderado' },
    { code: 'D1-C02', label: 'Aislamiento visible',                base: 'Bajo/Moderado' },
    { code: 'D1-C03', label: 'Irritabilidad intensa',              base: 'Moderado' },
    { code: 'D1-C04', label: 'Crisis emocional visible',           base: 'Moderado/Alto' },
    { code: 'D1-C05', label: 'Desregulación conductual observable',base: 'Moderado/Alto' },
    { code: 'D1-C06', label: 'Abandono de sala o actividad',       base: 'Bajo/Moderado' },
    { code: 'D1-C07', label: 'Cambio conductual brusco',           base: 'Bajo/Moderado' },
    { code: 'D1-C08', label: 'Verbalización de falta de expectativas', base: 'Moderado' },
  ],
  D2: [
    { code: 'D2-C01', label: 'Burlas repetitivas',                 base: 'Bajo/Moderado' },
    { code: 'D2-C02', label: 'Hostigamiento reiterado',            base: 'Moderado' },
    { code: 'D2-C03', label: 'Exclusión relacional sistemática',   base: 'Moderado/Alto' },
    { code: 'D2-C04', label: 'Difusión de rumores o humillación',  base: 'Moderado/Alto' },
    { code: 'D2-C05', label: 'Ciberacoso persistente',             base: 'Moderado/Alto' },
    { code: 'D2-C06', label: 'Violencia sexualizada digital',      base: 'Alto/Crítico', sentinel: 'EC-10' },
    { code: 'D2-C07', label: 'Intimidación grupal',                base: 'Alto' },
    { code: 'D2-C08', label: 'Hostigamiento físico',               base: 'Moderado/Alto' },
  ],
  D3: [
    { code: 'D3-C01', label: 'Amenaza específica de daño',         base: 'Alto/Crítico', sentinel: 'EC-01' },
    { code: 'D3-C02', label: 'Porte o acceso a objeto peligroso',  base: 'Crítico',      sentinel: 'EC-02' },
    { code: 'D3-C03', label: 'Planificación evidente',             base: 'Alto/Crítico', sentinel: 'EC-05' },
    { code: 'D3-C04', label: 'Búsqueda activa de víctima',         base: 'Alto' },
    { code: 'D3-C05', label: 'Escalamiento conductual agresivo',   base: 'Alto' },
    { code: 'D3-C06', label: 'Daño a propiedad',                   base: 'Moderado/Alto' },
    { code: 'D3-C07', label: 'Conflicto físico',                   base: 'Alto' },
    { code: 'D3-C08', label: 'Pelea grupal',                       base: 'Alto/Crítico', sentinel: 'EC-04' },
    { code: 'D3-C09', label: 'Agresión física grave',              base: 'Crítico',      sentinel: 'EC-03' },
    { code: 'D3-C10', label: 'Contenido de fascinación violenta con señal de riesgo', base: 'Alto' },
  ],
  D4: [
    { code: 'D4-C01', label: 'Verbaliza deseos de morir',          base: 'Crítico', sentinel: 'EC-07' },
    { code: 'D4-C02', label: 'Comunica intención de hacerse daño', base: 'Crítico', sentinel: 'EC-07' },
    { code: 'D4-C03', label: 'Evidencia de autolesión',            base: 'Crítico', sentinel: 'EC-06' },
    { code: 'D4-C04', label: 'Mensaje de despedida',               base: 'Crítico', sentinel: 'EC-08' },
    { code: 'D4-C05', label: 'Publicación de contenido autolesivo',base: 'Alto/Crítico' },
    { code: 'D4-C06', label: 'Desesperanza intensa verbalizada',   base: 'Alto' },
    { code: 'D4-C07', label: 'Conducta temeraria extrema',         base: 'Alto/Crítico' },
    { code: 'D4-C08', label: 'Retraimiento extremo',               base: 'Alto' },
  ],
  D5: [
    { code: 'D5-C01', label: 'Higiene descuidada persistente',     base: 'Bajo/Moderado' },
    { code: 'D5-C02', label: 'Hambre recurrente o falta de alimentación', base: 'Moderado' },
    { code: 'D5-C03', label: 'Signos compatibles con maltrato',    base: 'Alto/Crítico', sentinel: 'EC-12' },
    { code: 'D5-C04', label: 'Relato espontáneo de vulneración',   base: 'Alto/Crítico', sentinel: 'EC-11' },
    { code: 'D5-C05', label: 'Ausentismo crítico reiterado',       base: 'Moderado/Alto' },
    { code: 'D5-C06', label: 'Falta de adulto responsable disponible', base: 'Moderado/Alto' },
    { code: 'D5-C07', label: 'Abandono o desaparición del establecimiento', base: 'Crítico', sentinel: 'EC-13' },
    { code: 'D5-C08', label: 'Desprotección visible de necesidades básicas', base: 'Moderado' },
  ],
  D6: [
    { code: 'D6-C01', label: 'Polarización del curso',             base: 'Moderado/Alto' },
    { code: 'D6-C02', label: 'Subgrupos conflictivos persistentes',base: 'Moderado' },
    { code: 'D6-C03', label: 'Liderazgo coercitivo o negativo',    base: 'Alto' },
    { code: 'D6-C04', label: 'Normalización de insultos o burlas', base: 'Moderado' },
    { code: 'D6-C05', label: 'Testigos pasivos masivos',           base: 'Moderado/Alto' },
    { code: 'D6-C06', label: 'Rechazo grupal explícito',           base: 'Alto' },
    { code: 'D6-C07', label: 'Baja cohesión grupal',               base: 'Bajo/Moderado' },
    { code: 'D6-C08', label: 'Conflictos grupales persistentes',   base: 'Moderado/Alto' },
    { code: 'D6-C09', label: 'Baja percepción de seguridad observable', base: 'Moderado/Alto' },
    { code: 'D6-C10', label: 'Rotación disciplinaria alta',        base: 'Moderado' },
  ],
};

// ============ VARIABLES (§11–§17) ============
export const FRECUENCIA = [
  { v: 'primera_vez',  label: 'Primera vez',  hint: 'No existen registros previos.' },
  { v: 'ocasional',    label: 'Ocasional',    hint: 'Más de una vez, sin patrón claro.' },
  { v: 'reiterada',    label: 'Reiterada',    hint: 'Se repite en días o espacios distintos.' },
  { v: 'persistente',  label: 'Persistente',  hint: 'Patrón estable durante semanas.' },
];

export const INTENSIDAD = [
  { v: 'baja',  label: 'Baja',  hint: 'Leve, sin daño evidente.' },
  { v: 'media', label: 'Media', hint: 'Malestar visible o convivencia afectada.' },
  { v: 'alta',  label: 'Alta',  hint: 'Daño, amenaza o riesgo de escalamiento.' },
];

export const DURACION = [
  { v: 'episodio_unico', label: 'Episodio único' },
  { v: 'varios_dias',    label: 'Varios días' },
  { v: 'semanas',        label: 'Semanas' },
  { v: 'meses',          label: 'Meses' },
];

export const CONTEXTO = [
  { v: 'sala',         label: 'Sala de clases' },
  { v: 'recreo',       label: 'Recreo / patio' },
  { v: 'comedor',      label: 'Comedor' },
  { v: 'bano',         label: 'Baño / camarín' },
  { v: 'entrada',      label: 'Entrada / salida' },
  { v: 'transporte',   label: 'Transporte' },
  { v: 'redes',        label: 'Redes sociales' },
  { v: 'extracurricular', label: 'Actividad extracurricular' },
  { v: 'otro',         label: 'Otro' },
];

export const PARTICIPANTES = [
  { v: 'individual',     label: 'Individual' },
  { v: 'diada',          label: 'Díada' },
  { v: 'grupo_pequeno',  label: 'Grupo pequeño' },
  { v: 'curso_completo', label: 'Curso completo' },
  { v: 'sin_identificar',label: 'Sin identificar' },
];

export const FUENTE = [
  { v: 'observacion_directa', label: 'Observación directa' },
  { v: 'relato_estudiante',   label: 'Relato de estudiante' },
  { v: 'relato_adulto',       label: 'Relato de adulto' },
  { v: 'evidencia_digital',   label: 'Evidencia digital' },
  { v: 'registro_institucional', label: 'Registro institucional' },
  { v: 'otro',                label: 'Otro' },
];

// ============ MATRIZ DE PRIORIZACIÓN (§18–§23) ============
// Cálculo interno; nunca se muestra al observador.

const dañoPorIntensidad = { baja: 1, media: 2, alta: 3 };
const repeticionPorFrecuencia = { primera_vez: 0, ocasional: 1, reiterada: 2, persistente: 3 };
const urgenciaPorBase = (base = '') => {
  if (base.includes('Crítico')) return 3;
  if (base.includes('Alto')) return 2;
  if (base.includes('Moderado')) return 1;
  return 0;
};

export const NIVELES = {
  bajo:     { label: 'Bajo',     color: '#1E9F73', bg: '#DFF6EC' },
  moderado: { label: 'Moderado', color: '#C98718', bg: '#FFF1D6' },
  alto:     { label: 'Alto',     color: '#D86B26', bg: '#FFE0DA' },
  critico:  { label: 'Crítico',  color: '#C73838', bg: '#FCDCDC' },
};

/**
 * Calcula el nivel interno de priorización para auditoría/dashboard.
 * Si la conducta es evento centinela → Crítico automático.
 * NO debe mostrarse al observador.
 */
export const calcularNivel = (conducta, { intensidad, frecuencia }) => {
  if (!conducta) return { nivel: 'bajo', score: 0, isSentinel: false };
  if (conducta.sentinel) {
    return { nivel: 'critico', score: 9, isSentinel: true, sentinelCode: conducta.sentinel };
  }
  const daño = dañoPorIntensidad[intensidad] ?? 0;
  const repeticion = repeticionPorFrecuencia[frecuencia] ?? 0;
  const urgencia = urgenciaPorBase(conducta.base);
  const score = daño + repeticion + urgencia;
  let nivel = 'bajo';
  if (score >= 8) nivel = 'critico';
  else if (score >= 6) nivel = 'alto';
  else if (score >= 3) nivel = 'moderado';
  return { nivel, score, isSentinel: false };
};

// ============ CURSOS demo (no es invención de niveles, son cursos típicos) ============
export const CURSOS_DEMO = [
  '1°A', '1°B', '2°A', '2°B', '3°A', '3°B', '4°A', '4°B',
  '5°A', '5°B', '6°A', '6°B', '7°A', '7°B', '8°A', '8°B',
  'I°M A', 'I°M B', 'II°M A', 'II°M B', 'III°M A', 'III°M B', 'IV°M A', 'IV°M B',
];

// Opción especial: cuando el curso no se puede identificar.
export const CURSO_NO_IDENTIFICADO = 'No identificado';

// Etiquetas legibles para mostrar valores en resúmenes (no inventa niveles,
// sólo "pretty print" de los valores ya definidos arriba).
export const VALUE_LABELS = {
  // frecuencia
  primera_vez: 'Primera vez',
  ocasional: 'Ocasional',
  reiterada: 'Reiterada',
  persistente: 'Persistente',
  // intensidad
  baja: 'Baja',
  media: 'Media',
  alta: 'Alta',
  // duración
  episodio_unico: 'Episodio único',
  varios_dias: 'Varios días',
  semanas: 'Semanas',
  meses: 'Meses',
  // contexto
  sala: 'Sala de clases',
  recreo: 'Recreo / patio',
  comedor: 'Comedor',
  bano: 'Baño / camarín',
  entrada: 'Entrada / salida',
  transporte: 'Transporte',
  redes: 'Redes sociales',
  extracurricular: 'Actividad extracurricular',
  otro: 'Otro',
  // participantes
  individual: 'Individual',
  diada: 'Díada (dos personas)',
  grupo_pequeno: 'Grupo pequeño',
  curso_completo: 'Curso completo',
  sin_identificar: 'Sin identificar',
  // fuente
  observacion_directa: 'Observación directa',
  relato_estudiante: 'Relato de estudiante',
  relato_adulto: 'Relato de adulto',
  evidencia_digital: 'Evidencia digital',
  registro_institucional: 'Registro institucional',
};

export const prettyValue = (v) => VALUE_LABELS[v] || v || '—';

// ============ ESTADOS DEL REGISTRO ============
// Diseñado para sincronizar con la BD oficial (Sprint 3+).
// Los estados 'en_revision', 'en_seguimiento' y 'cerrado' SOLO los puede
// cambiar el Encargado de Convivencia desde el dashboard web.
export const REGISTRO_ESTADOS = {
  registrado:        { label: 'Registrado',          color: '#5346E6', bg: '#EEEAFE' },
  editado:           { label: 'Editado',             color: '#2F6BFF', bg: '#DCE7FF' },
  marcado_erroneo:   { label: 'Marcado como erróneo', color: '#C73838', bg: '#FCDCDC' },
  en_revision:       { label: 'En revisión',         color: '#C98718', bg: '#FFF1D6' },
  en_seguimiento:    { label: 'En seguimiento',      color: '#D24B8A', bg: '#FCE4EF' },
  cerrado:           { label: 'Cerrado',             color: '#1E9F73', bg: '#DFF6EC' },
};

// Estado por defecto al crear una observación.
export const ESTADO_INICIAL = 'registrado';

// Estados que permiten edición desde el móvil.
// Una vez que Encargado/a la revisa (estado != registrado/editado), no se edita.
export const ESTADOS_EDITABLES = ['registrado', 'editado'];

// ============ MODO DE OPERACIÓN ============
// 'operativo' (datos oficiales) | 'induccion' (entrenamiento, base separada).
export const APP_MODES = {
  operativo: {
    label: 'Registro Oficial',
    description: 'Los registros se guardan en la base oficial del establecimiento.',
    badge: 'Oficial',
    color: '#1E9F73',
    bg: '#DFF6EC',
    dot: '🟢',
  },
  induccion: {
    label: 'Modo Inducción',
    description: 'Capacitación. Los registros NO se mezclan con los oficiales.',
    badge: 'Capacitación',
    color: '#C98718',
    bg: '#FFF1D6',
    dot: '🟡',
  },
};

// ============ ROLES (§39) ============
export const ROLES = [
  { v: 'profesor_jefe',        label: 'Profesor/a jefe' },
  { v: 'profesor_asignatura',  label: 'Profesor/a de asignatura' },
  { v: 'inspector',            label: 'Inspector/a' },
  { v: 'asistente',            label: 'Asistente de la educación' },
  { v: 'orientador',           label: 'Orientador/a' },
  { v: 'convivencia',          label: 'Encargado/a de convivencia' },
  { v: 'directivo',            label: 'Equipo directivo' },
];

export default {
  DOMAINS,
  CONDUCTS,
  FRECUENCIA,
  INTENSIDAD,
  DURACION,
  CONTEXTO,
  PARTICIPANTES,
  FUENTE,
  CURSOS_DEMO,
  ROLES,
  NIVELES,
  calcularNivel,
};
